import { Box, makeStyles, Typography } from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import LayersClearIcon from '@material-ui/icons/LayersClear';
import { FC, ReactNode } from 'react';
import { WbButton } from '../WbButton';
import { WbNoData } from '../WbNoData';
import {
  EnvironmentAccessState,
  useEnvironmentsContext,
} from '../../context/EnvironmentsContext';

interface Props {
  access?: EnvironmentAccessState;
  children?: ReactNode;
  onRetry?: () => void;
  fillAvailableViewport?: boolean;
}

const useStyles = makeStyles(theme => ({
  emptyStateContainer: {
    gridColumn: '1 / -1',
    gridRow: '2 / -1',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  availableViewport: {
    width: '100%',
    minHeight: 'calc(100vh - 200px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    width: '100%',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  emptyStateTitle: {
    fontSize: '2rem',
    fontWeight: 700,
  },
  emptyStateIcon: {
    width: 'min(250px, 40vw)',
    height: 'min(250px, 40vw)',
    border: `3px dashed ${theme.palette.softened.primary}`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateIconGraphic: {
    width: '70%',
    height: '70%',
    color: theme.palette.primary.light,
  },
  emptyStateText: {
    maxWidth: 900,
    fontSize: '1.15rem',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minHeight: 28,
    margin: theme.spacing(0.5, 3),
    padding: theme.spacing(0, 1),
    backgroundColor: theme.palette.bkg.warning,
    border: `1px solid ${theme.palette.warning.dark}`,
    borderRadius: 4,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    '& svg': {
      color: theme.palette.warning.dark,
      fontSize: 18,
    },
    '& p': {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium as number,
    },
  },
}));

export const WbEnvironmentEmptyState: FC = () => {
  const classes = useStyles();

  return (
    <Box className={classes.emptyStateContainer}>
      <Box className={classes.emptyState}>
        <Typography className={classes.emptyStateTitle} variant="h4">
          You don&apos;t have access to any environment.
        </Typography>
        <Box className={classes.emptyStateIcon}>
          <LayersClearIcon className={classes.emptyStateIconGraphic} />
        </Box>
        <Typography className={classes.emptyStateText}>
          This page is empty because you don&apos;t have access to any
          environment, so you can&apos;t view its content.
          <br />
          Please contact the platform team for more information and assistance.
        </Typography>
      </Box>
    </Box>
  );
};

export const WbEnvironmentWarning: FC = () => {
  const classes = useStyles();

  return (
    <Box className={classes.warning} role="status">
      <WarningOutlinedIcon fontSize="small" />
      <Typography variant="body2">No accessible environments.</Typography>
    </Box>
  );
};

export const WbEnvironmentAccessState: FC<Props> = ({
  access: accessOverride,
  children,
  onRetry: retryOverride,
  fillAvailableViewport = false,
}) => {
  const classes = useStyles();
  const { environmentAccess, retryEnvironmentAccess } =
    useEnvironmentsContext();
  const access = accessOverride ?? environmentAccess;
  const onRetry = retryOverride ?? retryEnvironmentAccess;

  if (access.status === 'ready') return <>{children}</>;
  let fallback: ReactNode;
  if (access.status === 'empty') {
    fallback = <WbEnvironmentEmptyState />;
  } else if (access.status === 'loading') {
    fallback = (
      <WbNoData text="Loading accessible environments..." fillContainer />
    );
  } else {
    fallback = (
      <WbNoData
        header={
          access.status === 'authentication-error'
            ? 'Environment access unavailable'
            : 'Unable to load environments'
        }
        text={
          access.status === 'authentication-error'
            ? 'Your identity could not be authenticated for environment access.'
            : 'Environment access could not be loaded. Retry to continue.'
        }
        primaryAction={
          onRetry ? <WbButton label="Retry" onClick={onRetry} /> : undefined
        }
        fillContainer
      />
    );
  }

  return fillAvailableViewport ? (
    <Box className={classes.availableViewport}>{fallback}</Box>
  ) : (
    <>{fallback}</>
  );
};
